import React, { useState, useEffect } from 'react'
import LocationForm from './LocationForm'
import axios from 'axios'
import omit from 'lodash/omit'
import { t } from '../../i18n'
import KitchenConfigurationWarningModal from './KitchenConfigurationWarningModal'
import DisableCookingUnitConfirmModal from './DisableCookingUnitConfirmModal'
import { newlyDisabledCookingUnitSlotNumbers } from './kitchenConfigurationHelper'

const LocationEditForm = (props) => {
  const [location, setLocation] = useState(null)
  const [saveSuccessful, setSaveSuccessful] = useState(null)
  const [saveText, setSaveText] = useState('')
  const [configWarnings, setConfigWarnings] = useState([])
  const [initialCookingUnits, setInitialCookingUnits] = useState([])
  const [disableConfirm, setDisableConfirm] = useState(null)

  const [kitchenConfiguration, setKitchenConfiguration] = useState({
    fixed_container_slots: [],
    mobile_container_slots: [],
    plate_stands: [],
    cooking_units: [],
    locker_slots: []
  })

  const [kitchenConfigurationErrors, setKitchenConfigurationErrors] = useState({
    fixed_container_slots: false,
    mobile_container_slots: false,
    plate_stands: false,
    cooking_units: false,
    locker_slots: false
  })

  useEffect(() => {
    axios
      .get(Routes.api_internal_location_path(props.id, { format: 'json' }))
      .then((resp) => {
        setKitchenConfiguration(resp.data.kitchen_configuration)
        setInitialCookingUnits(resp.data.kitchen_configuration.cooking_units)
        setLocation({...omit(resp.data, 'kitchen_configuration'), attachment: null})
      })
  }, [])

  const providersWithoutProviderType = (orderProviders) => {
    return orderProviders.map((provider) => {
      const { provider_type, destroyable, ...rest } = provider
      return rest
    })
  }

  const saveLocation = (location, onFinished) => {
    const newlyDisabled = newlyDisabledCookingUnitSlotNumbers(kitchenConfiguration.cooking_units, initialCookingUnits)
    if (newlyDisabled.length === 0) {
      return submitLocation(location, onFinished)
    }

    axios
      .get(Routes.taken_cooking_units_api_internal_location_path(location.id, {
        slot_numbers: newlyDisabled, format: 'json'
      }))
      .then((resp) => {
        const takenUnits = resp.data.taken_cooking_units || []
        if (takenUnits.length > 0) {
          setDisableConfirm({ takenUnits, location, onFinished })
        } else {
          submitLocation(location, onFinished)
        }
      })
      // The confirm is informational only — the backend handles an in-use disable safely.
      .catch(() => submitLocation(location, onFinished))
  }

  const submitLocation = (location, onFinished) => {
    const url = Routes.api_internal_location_path(location.id, { format: 'json' });

    const formData = buildFormData(location);

    axios
      .patch(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((resp) => handleSaveSuccess(resp, onFinished))
      .catch((error) => handleSaveError(error, onFinished));
  };

  const buildFormData = (location) => {
    const formData = new FormData();

    const basicAttributes = [
      'name',
      'currency_id',
      'temperature_scale',
      'weight_scale',
      'webhook_url',
      'nonstop_working_hours',
      'working_start_hour',
      'working_end_hour',
      'robot_connection_url',
      'robot_connection_username',
      'robot_connection_password',
      'robot_certificate',
      'robot_private_key',
      'address',
      'date_format',
      'time_format',
      'timezone',
      'sensor_report_emails',
      'critical_issue_phone_numbers',
      'sensor_connection_url',
      'ai_platform_enabled',
      'ai_platform_workspace_id'
    ];

    basicAttributes.forEach((attr) => {
      if (location[attr] != null && location[attr] != undefined) {
        formData.append(attr, location[attr]);
      }
    })

    if (location.sensor_report_emails != null && location.sensor_report_emails != undefined) {
      formData.append('sensor_report_emails', JSON.stringify(location.sensor_report_emails));
    }

    if (location.critical_issue_phone_numbers != null && location.critical_issue_phone_numbers != undefined) {
      formData.append('critical_issue_phone_numbers', JSON.stringify(location.critical_issue_phone_numbers));
    }

    if (location.order_provider_locations_attributes) {
      formData.append(
        'order_provider_locations_attributes',
        JSON.stringify(providersWithoutProviderType(location.order_provider_locations_attributes))
      );
    }

    if (kitchenConfiguration) {
      formData.append('kitchen_configuration', JSON.stringify(kitchenConfiguration));
    }

    if (location.attachment) {
      formData.append('attachment', location.attachment);
    }

    return formData;
  };

  const handleSaveSuccess = (resp, onFinished) => {
    setSaveSuccessful(true);
    setSaveText(t('save_successful', { scope: 'locations' }));
    onFinished(true, null);
    setLocation({ ...resp.data.location, attachment: null });
    setInitialCookingUnits(kitchenConfiguration.cooking_units);

    if (resp.data.warnings) {
      setConfigWarnings(resp.data.warnings);
    }
  };

  const handleSaveError = (error, onFinished) => {
    setSaveSuccessful(false);
    setSaveText(t('save_failed', { scope: 'locations' }));
    onFinished(false, error.response?.data);
  };

  const renderSaveMessage = () =>
    saveSuccessful === null ? null : (
      <div className={`alert alert-${saveSuccessful ? 'success' : 'danger'}`}>
        {saveText}
      </div>
    )

  if (!location) {
    return <></>
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        {renderSaveMessage()}
        <LocationForm
          location={location}
          kitchenConfiguration={kitchenConfiguration}
          setKitchenConfiguration={setKitchenConfiguration}
          kitchenConfigurationOptions={props.kitchenConfigurationOptions}
          kitchenConfigurationErrors={kitchenConfigurationErrors}
          setKitchenConfigurationErrors={setKitchenConfigurationErrors}
          orderProviderOptions={props.orderProviderOptions}
          currencies={props.currencies}
          dateFormats={props.dateFormats}
          timeFormats={props.timeFormats}
          timezones={props.timezones}
          timezoneMappings={props.timezoneMappings}
          onSubmit={saveLocation}
          action="update"
        />
      </div>
      { configWarnings.length > 0 &&
        <KitchenConfigurationWarningModal
          onClose={() => setConfigWarnings([])}
          warnings={configWarnings}
        />
      }
      { disableConfirm &&
        <DisableCookingUnitConfirmModal
          slotNumbers={disableConfirm.takenUnits}
          onConfirm={() => {
            submitLocation(disableConfirm.location, disableConfirm.onFinished)
            setDisableConfirm(null)
          }}
          onCancel={() => {
            disableConfirm.onFinished(true, null)
            setDisableConfirm(null)
          }}
        />
      }
    </div>
  )
}

export default LocationEditForm
